import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';

const LanguageSwitcher = ({ onToggle, isOtherOpen }) => {
  const { i18n } = useTranslation();
  const { changeLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const languages = [
    { code: 'en', name: 'ENG', flag: 'https://flagcdn.com/w40/us.png' },
    { code: 'fr', name: 'FRA', flag: 'https://flagcdn.com/w40/fr.png' },
    { code: 'de', name: 'DEU', flag: 'https://flagcdn.com/w40/de.png' },
    { code: 'ru', name: 'RUS', flag: 'https://flagcdn.com/w40/ru.png' },
  ];

  const handleLanguageChange = (langCode) => {
    changeLanguage(langCode);
    setIsOpen(false);
  };

  const handleToggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const currentLanguage =
    languages.find((lang) => lang.code === i18n.language) || languages[0];

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleToggle}
        className="flex items-center gap-1 bg-[#F5F5F5] rounded-full pl-1 pr-2 py-1 transition duration-200"
      >
        <svg
          width="25"
          height="25"
          viewBox="0 0 25 25"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12.085 22.7383C10.7183 22.7383 9.42663 22.4756 8.20996 21.9503C6.99329 21.4249 5.93096 20.7083 5.02296 19.8003C4.11496 18.8923 3.39829 17.8299 2.87296 16.6133C2.34763 15.3966 2.08496 14.1049 2.08496 12.7383C2.08496 11.3549 2.34763 10.0593 2.87296 8.85128C3.39829 7.64328 4.11496 6.58495 5.02296 5.67628C5.93096 4.76761 6.99329 4.05095 8.20996 3.52628C9.42663 3.00161 10.7183 2.73895 12.085 2.73828C13.4683 2.73828 14.7643 3.00095 15.973 3.52628C17.1816 4.05161 18.2396 4.76828 19.147 5.67628C20.0543 6.58428 20.771 7.64261 21.297 8.85128C21.823 10.0599 22.0856 11.3556 22.085 12.7383C22.085 14.1049 21.8223 15.3966 21.297 16.6133C20.7716 17.8299 20.055 18.8926 19.147 19.8013C18.239 20.7099 17.1806 21.4266 15.972 21.9513C14.7633 22.4759 13.4676 22.7383 12.085 22.7383ZM12.085 20.6883C12.5183 20.0883 12.8933 19.4633 13.21 18.8133C13.5266 18.1633 13.785 17.4716 13.985 16.7383H10.185C10.385 17.4716 10.6433 18.1633 10.96 18.8133C11.2766 19.4633 11.6516 20.0883 12.085 20.6883ZM9.48496 20.2883C9.18496 19.7383 8.92263 19.1673 8.69796 18.5753C8.47329 17.9833 8.28563 17.3709 8.13496 16.7383H5.18496C5.66829 17.5716 6.27263 18.2966 6.99796 18.9133C7.72329 19.5299 8.55229 19.9883 9.48496 20.2883ZM14.685 20.2883C15.6183 19.9883 16.4476 19.5299 17.173 18.9133C17.8983 18.2966 18.5023 17.5716 18.985 16.7383H16.035C15.885 17.3716 15.6976 17.9843 15.473 18.5763C15.2483 19.1683 14.9856 19.7389 14.685 20.2883ZM4.33496 14.7383H7.73496C7.68496 14.4049 7.64763 14.0759 7.62296 13.7513C7.59829 13.4266 7.58563 13.0889 7.58496 12.7383C7.58429 12.3876 7.59696 12.0503 7.62296 11.7263C7.64896 11.4023 7.68629 11.0729 7.73496 10.7383H4.33496C4.25163 11.0716 4.18929 11.4009 4.14796 11.7263C4.10663 12.0516 4.08563 12.3889 4.08496 12.7383C4.08429 13.0876 4.10529 13.4253 4.14796 13.7513C4.19063 14.0773 4.25296 14.4063 4.33496 14.7383ZM9.73496 14.7383H14.435C14.485 14.4049 14.5226 14.0759 14.548 13.7513C14.5733 13.4266 14.5856 13.0889 14.585 12.7383C14.5843 12.3876 14.5716 12.0503 14.547 11.7263C14.5223 11.4023 14.485 11.0729 14.435 10.7383H9.73496C9.68496 11.0716 9.64763 11.4009 9.62296 11.7263C9.59829 12.0516 9.58563 12.3889 9.58496 12.7383C9.58429 13.0876 9.59696 13.4253 9.62296 13.7513C9.64896 14.0773 9.68629 14.4063 9.73496 14.7383ZM16.435 14.7383H19.835C19.9183 14.4049 19.981 14.0759 20.023 13.7513C20.065 13.4266 20.0856 13.0889 20.085 12.7383C20.0843 12.3876 20.0636 12.0503 20.023 11.7263C19.9823 11.4023 19.9196 11.0729 19.835 10.7383H16.435C16.485 11.0716 16.5226 11.4009 16.548 11.7263C16.5733 12.0516 16.5856 12.3889 16.585 12.7383C16.5843 13.0876 16.5716 13.4253 16.547 13.7513C16.5223 14.0773 16.485 14.4063 16.435 14.7383ZM16.035 8.73828H18.985C18.5016 7.90495 17.8976 7.17995 17.173 6.56328C16.4483 5.94661 15.619 5.48828 14.685 5.18828C14.985 5.73828 15.2476 6.30928 15.473 6.90128C15.6983 7.49328 15.8856 8.10561 16.035 8.73828ZM10.185 8.73828H13.985C13.785 8.00495 13.5266 7.31328 13.21 6.66328C12.8933 6.01328 12.5183 5.38828 12.085 4.78828C11.6516 5.38828 11.2766 6.01328 10.96 6.66328C10.6433 7.31328 10.385 8.00495 10.185 8.73828ZM5.18496 8.73828H8.13496C8.28496 8.10495 8.47263 7.49228 8.69796 6.90028C8.92329 6.30828 9.18563 5.73761 9.48496 5.18828C8.55163 5.48828 7.72229 5.94661 6.99696 6.56328C6.27163 7.17995 5.66763 7.90495 5.18496 8.73828Z"
            fill="black"
          />
        </svg>
        <h3 className="text-[14px] leading-[16px] font-medium text-[#000000]">
          {currentLanguage.name}
        </h3>
      </button>

      {isOpen && (
        <div className="absolute right-0 bottom-full mb-2 md:bottom-auto md:top-full md:mt-2 w-32 sm:w-40 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="py-2">
            {languages.map((language) => (
              <button
                key={language.code}
                onClick={() => handleLanguageChange(language.code)}
                className={`w-full flex items-center space-x-3 pl-4 pr-2 sm:px-4 py-2 text-left hover:bg-gray-100 transition duration-200 ${i18n.language === language.code
                  ? 'bg-gray-50 text-black'
                  : 'text-gray-700'
                  }`}
              >
                <img
                  src={language.flag}
                  alt={`${language.name} flag`}
                  className="w-5 h-5 object-cover rounded-full"
                />
                <span className="text-sm font-medium">{language.name}</span>
                {i18n.language === language.code && (
                  <svg
                    className="min-w-3 h-3 ml-auto text-black"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
